import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { glossary } from '#src/lib/glossary.ts';

import { AddressStep } from './AddressStep/index.tsx';
import { KycStep } from './KycStep/index.tsx';
import { MangopayKycRequiredStep } from './MangopayKycRequiredStep/index.tsx';
import { OffRampStepLayout } from './OffRampStepLayout/index.tsx';
import { TermsStep } from './TermsStep/index.tsx';
import type { OffRampOnboardingState } from './useOffRampOnboardingState.tsx';
import { useOffRampOnboardingState } from './useOffRampOnboardingState.tsx';

const OffRampKycOnboardingStep = {
  TERMS: 'TERMS',
  ADDRESS: 'ADDRESS',
  KYC: 'KYC',
  COMPLETED: 'COMPLETED',
} as const;

type OffRampKycOnboardingStep =
  (typeof OffRampKycOnboardingStep)[keyof typeof OffRampKycOnboardingStep];

type Props = {
  onDone?: () => void;
};

const initialStep = (
  state: OffRampOnboardingState
): OffRampKycOnboardingStep | null => {
  switch (state.status) {
    case 'notEnrolled':
      return OffRampKycOnboardingStep.TERMS;
    case 'kycPending':
    case 'terminalFailure':
      return OffRampKycOnboardingStep.KYC;
    case 'active':
      return OffRampKycOnboardingStep.COMPLETED;
    default:
      return null;
  }
};

export const OffRampKycOnboarding = ({ onDone }: Props) => {
  const {
    flags: { enableOffRamp = false },
  } = useFeatureFlags();
  const { state, refetch } = useOffRampOnboardingState();

  const [step, setStep] = useState<OffRampKycOnboardingStep | null>(null);
  const [signedAgreementId, setSignedAgreementId] = useState<string | null>(
    null
  );

  if (!enableOffRamp) {
    return (
      <OffRampStepLayout title={<FormattedMessage defaultMessage="Cash out" />}>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Cash out isn't available on your account yet." />
        </BodyM>
      </OffRampStepLayout>
    );
  }

  if (state.status === 'loading') {
    return <LoadingIndicator fullHeight />;
  }

  if (state.status === 'signedOut') {
    return null;
  }

  if (state.status === 'mangopayKycRequired') {
    return <MangopayKycRequiredStep onWalletChange={refetch} />;
  }

  const currentStep = step ?? initialStep(state);

  if (currentStep === OffRampKycOnboardingStep.ADDRESS && signedAgreementId) {
    return (
      <AddressStep
        signedAgreementId={signedAgreementId}
        onComplete={() => setStep(OffRampKycOnboardingStep.KYC)}
      />
    );
  }

  if (currentStep === OffRampKycOnboardingStep.KYC) {
    return (
      <KycStep
        onComplete={() => setStep(OffRampKycOnboardingStep.COMPLETED)}
        onClose={onDone}
      />
    );
  }

  if (currentStep === OffRampKycOnboardingStep.COMPLETED) {
    return (
      <OffRampStepLayout
        title={<FormattedMessage defaultMessage="You're all set" />}
        description={
          <FormattedMessage defaultMessage="Your identity is verified. You can now cash out to your bank account." />
        }
      >
        {!!onDone && (
          <Vertical gap={2}>
            <Button size="medium" fullWidth onClick={onDone}>
              <FormattedMessage {...glossary.continue} />
            </Button>
          </Vertical>
        )}
      </OffRampStepLayout>
    );
  }

  return (
    <TermsStep
      onComplete={id => {
        setSignedAgreementId(id);
        setStep(OffRampKycOnboardingStep.ADDRESS);
      }}
    />
  );
};
